import React, { useMemo, useRef, useState } from "react";
import { X, Sparkles, Send, Square } from "lucide-react";

const SYSTEM_PROMPT =
  "You are a helpful assistant. Answer clearly and concisely. Use plain text (no markdown) unless the user asks otherwise.";

function safeParseJson(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

export default function LMStudioChatModal({ isOpen, onClose }) {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);

  const openaiMessages = useMemo(() => {
    const history = messages
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map((m) => ({ role: m.role, content: m.content || "" }));
    return [{ role: "system", content: SYSTEM_PROMPT }, ...history].slice(-20);
  }, [messages]);

  const stop = () => {
    try {
      abortRef.current?.abort();
    } catch {
      // ignore
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isSending) return;

    setError(null);
    setInput("");

    const userMsg = { id: `u-${Date.now()}`, role: "user", content: text };
    const assistantId = `a-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const assistantMsg = { id: assistantId, role: "assistant", content: "", thinking: "" };

    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setIsSending(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch("/api/lmstudio-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: openaiMessages.concat([{ role: "user", content: text }]),
          temperature: 0.7,
          stream: true,
        }),
        signal: controller.signal,
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => null);
        throw new Error(errJson?.error || errJson?.detail || "LM Studio request failed");
      }

      const contentType = res.headers.get("content-type") || "";
      if (!contentType.includes("text/event-stream")) {
        const data = await res.json().catch(() => null);
        const finalContent = data?.content || "";
        setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content: finalContent } : m)));
        setIsSending(false);
        abortRef.current = null;
        return;
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error("No stream body");

      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = buffer.replace(/\r\n/g, "\n");

        // SSE events are separated by a blank line.
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";

        for (const part of parts) {
          const lines = part.split("\n").map((l) => l.trim());
          for (const line of lines) {
            if (!line.startsWith("data:")) continue;
            const dataStr = line.replace(/^data:\s*/, "");
            if (!dataStr) continue;
            if (dataStr === "[DONE]") {
              setIsSending(false);
              abortRef.current = null;
              return;
            }
            const payload = safeParseJson(dataStr);
            if (!payload) continue;

            const delta = payload?.choices?.[0]?.delta || {};
            const contentDelta = delta?.content || "";
            const reasoningDelta = delta?.reasoning || delta?.thinking || "";

            if (reasoningDelta) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, thinking: (m.thinking || "") + reasoningDelta }
                    : m
                )
              );
            }
            if (contentDelta) {
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? { ...m, content: (m.content || "") + contentDelta } : m))
              );
            }
          }
        }
      }

      setIsSending(false);
      abortRef.current = null;
    } catch (err) {
      if (controller.signal.aborted) {
        setIsSending(false);
        abortRef.current = null;
        return;
      }
      console.error("[LMStudioChatModal] error:", err);
      setError(err?.message || "LM Studio chat failed");
      setIsSending(false);
      abortRef.current = null;
    }
  };

  const closeIfAllowed = () => {
    if (isSending) return;
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={closeIfAllowed}
      style={styles.overlay}
    >
      <div onClick={(e) => e.stopPropagation()} style={styles.panel}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <Sparkles size={18} color="#D4AF37" />
            <div style={styles.title}>LM Studio Chat</div>
          </div>
          <button type="button" onClick={closeIfAllowed} style={styles.closeBtn} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.messages} aria-label="Chat messages">
          {messages.length === 0 ? (
            <div style={styles.empty}>
              <Square size={14} style={{ opacity: 0.6 }} />
              <div style={styles.emptyText}>Type a prompt and press Send.</div>
            </div>
          ) : (
            messages.map((m) => (
              <div key={m.id} style={m.role === "user" ? styles.userBubble : styles.assistantBubble}>
                <div style={styles.role}>{m.role === "user" ? "You" : "Assistant"}</div>
                {m.role === "assistant" && m.thinking ? <div style={styles.thinking}>thinking: {m.thinking}</div> : null}
                <div style={styles.content}>{m.content}</div>
              </div>
            ))
          )}
        </div>

        <div style={styles.composer}>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isSending}
            placeholder="Ask LM Studio…"
            style={styles.input}
            rows={2}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                handleSend();
              }
              if (e.key === "Escape") {
                closeIfAllowed();
              }
            }}
          />
          <div style={styles.actions}>
            {isSending ? (
              <button type="button" onClick={stop} style={{ ...styles.sendBtn, ...styles.stopBtn }}>
                Stop
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSend}
                disabled={!input.trim() || isSending}
                style={{
                  ...styles.sendBtn,
                  ...(input.trim() && !isSending ? {} : { opacity: 0.6, cursor: "not-allowed" }),
                }}
              >
                <Send size={18} />
                <span style={{ marginLeft: 8 }}>Send</span>
              </button>
            )}
          </div>
        </div>

        <div style={styles.hintRow}>
          <span style={styles.hint}>Ctrl/Cmd+Enter to send</span>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.72)",
    zIndex: 12000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 14,
  },
  panel: {
    width: "min(820px, 100%)",
    height: "min(78vh, 720px)",
    background: "rgba(26, 16, 12, 0.98)",
    borderRadius: 16,
    border: "2px solid rgba(212,175,55,0.25)",
    boxShadow: "0 24px 80px rgba(0,0,0,0.85)",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    padding: "14px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 10 },
  title: { fontWeight: 900, color: "#D4AF37", letterSpacing: 0.3 },
  closeBtn: { background: "none", border: "none", color: "rgba(255,255,255,0.75)", cursor: "pointer", padding: 6 },
  error: { padding: "10px 14px", background: "rgba(255, 180, 180, 0.12)", color: "#ffb4b4", borderBottom: "1px solid rgba(255,255,255,0.06)" },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  empty: { display: "flex", alignItems: "center", gap: 10, opacity: 0.75, paddingTop: 10 },
  emptyText: { fontSize: 14 },
  role: { fontSize: 12, color: "rgba(255,255,255,0.55)", marginBottom: 6 },
  content: { whiteSpace: "pre-wrap", lineHeight: 1.4, fontSize: 14 },
  userBubble: {
    alignSelf: "flex-end",
    maxWidth: "86%",
    padding: "10px 12px",
    borderRadius: 14,
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  assistantBubble: {
    alignSelf: "flex-start",
    maxWidth: "86%",
    padding: "10px 12px",
    borderRadius: 14,
    background: "rgba(212,175,55,0.08)",
    border: "1px solid rgba(212,175,55,0.12)",
  },
  thinking: { fontSize: 12, color: "rgba(212,175,55,0.75)", marginBottom: 6, fontStyle: "italic", whiteSpace: "pre-wrap" },
  composer: {
    borderTop: "1px solid rgba(255,255,255,0.08)",
    padding: 12,
    display: "flex",
    gap: 10,
    alignItems: "flex-end",
  },
  input: {
    flex: 1,
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 12,
    color: "#fff",
    padding: "10px 12px",
    outline: "none",
    resize: "none",
    minHeight: 44,
    maxHeight: 140,
  },
  actions: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 },
  sendBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0,
    width: 140,
    height: 44,
    borderRadius: 12,
    border: "1px solid rgba(212,175,55,0.35)",
    background: "rgba(212,175,55,0.15)",
    color: "#D4AF37",
    cursor: "pointer",
    fontWeight: 800,
  },
  stopBtn: {
    width: 120,
    background: "rgba(255,255,255,0.06)",
    borderColor: "rgba(255,255,255,0.18)",
    color: "rgba(255,255,255,0.75)",
  },
  hintRow: { padding: "0 14px 12px", fontSize: 12, color: "rgba(255,255,255,0.55)" },
  hint: { opacity: 0.9 },
};

